import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSwellMaps } from '../data/swellMaps';
import {
  useForecastSpots,
  ensureDefaultSpots,
  ensureSpotCalibrations,
  addSpot,
  removeSpot,
  loadSpotForecast,
} from '../data/forecastSpots';
import { SpotSettings, strikeFor } from './Forecast';
import BasinImageryTiles from '../components/BasinImageryTiles';
import {
  groupByDay,
  dirLabel,
  compass16,
  arrowRotation,
  swellColor,
  fmtFt,
  fmtKn,
  fmtPeriod,
  kmhToKn,
  sampleAt,
} from '../utils/conditions';
import {
  evaluateStrike,
  evaluateAlerts,
  resolveWindArc,
  resolveSwellArc,
  swellDirOk,
  surfFaceFt,
  combinedSurf,
  hourPeriodS,
  hourSwellDir,
  hourWindKn,
  windQuality,
  WIND_QUALITY_COLOR,
  WIND_QUALITY_LABEL,
  tideBand,
  tideOk,
  tideStageLabel,
  TIDE_MODE_LABEL,
  densifyForecastHours,
} from '../utils/forecast';
import { useAuth } from '../AuthContext';
import SITE from '../site';

// Strike-mission board for the Rote area (Nemberala / Bo'a / west Timor):
// a high-res regional swell map (Copernicus MFWAM 1/12° grid, 10 m coastline,
// rendered server-side like the basin maps, with a GEBCO bathymetry layer) +
// an hourly today/tomorrow board + a 16-day strike calendar strip per spot,
// gated by each spot's swell-direction / wind / tide preferences.
//
// The page lists every tracked forecast spot whose region mentions "Rote", so
// adding more spots (Suckies, Bommie, Oe Mau…) is just "+ Add spot" with their
// preferences — no code change.

const FORECAST_DAYS = 16;
const CONFIDENCE_DAYS = 10; // past this the wave models are lower confidence
const SPEEDS = [0.5, 1, 2, 4];
// GEBCO bathymetry stills (CDN) — translucent overlays over swell/wind video.
const BATHY_URL_REGIONAL = 'https://cdn.chadha.surf/swellmaps/static/rote-bathy.png';
const BATHY_URL_SW = 'https://cdn.chadha.surf/swellmaps/static/bathy-rote-sw.png';

const ZOOM_MIN = 1;
const ZOOM_MAX = 4;
const BATHY_OPACITY_DEFAULT = 0.4;
const SAT_OPACITY_DEFAULT = 0.45;

const isRoteSpot = (s) => /rote/i.test(s.region || '');

// ------------------------- shared forecast loading -------------------------
function useRoteForecasts(spots) {
  const [map, setMap] = useState({});
  const key = useMemo(() => (spots || []).map((s) => s.id).join('|'), [spots]);
  useEffect(() => {
    if (!spots || !spots.length) { setMap({}); return undefined; }
    let alive = true;
    setMap((prev) => Object.fromEntries(spots.map((s) => [s.id, prev[s.id]?.cond ? prev[s.id] : { loading: true }])));
    const queue = [...spots];
    let active = 0;
    const pump = () => {
      if (!alive) return;
      while (active < 3 && queue.length) {
        const s = queue.shift();
        active += 1;
        loadSpotForecast(s, FORECAST_DAYS)
          .then((cond) => alive && setMap((m) => ({ ...m, [s.id]: { cond } })))
          .catch((e) => alive && setMap((m) => ({ ...m, [s.id]: { err: e.message } })))
          .finally(() => { active -= 1; pump(); });
      }
    };
    pump();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return map;
}

// --------------------------- regional swell map ---------------------------
// Project a spot into the rendered frame (same linear mapping as the backend).
function projectSpot(spot, doc) {
  const { latMin, latMax, lonMin, lonMax } = doc || {};
  if (![latMin, latMax, lonMin, lonMax].every(Number.isFinite)) return null;
  if (!Number.isFinite(spot.lat) || !Number.isFinite(spot.lng)) return null;
  if (spot.lat < latMin || spot.lat > latMax) return null;
  let clon = spot.lng;
  while (clon < lonMin) clon += 360;
  while (clon > lonMax) clon -= 360;
  if (clon < lonMin || clon > lonMax) return null;
  return {
    xFrac: (clon - lonMin) / (lonMax - lonMin || 1),
    yFrac: (latMax - spot.lat) / (latMax - latMin || 1),
  };
}

function frameTimeLabel(sec, tz = 'Asia/Makassar') {
  const d = new Date(sec * 1000);
  const day = d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', timeZone: tz });
  const hr = d.toLocaleTimeString([], { hour: 'numeric', timeZone: tz });
  return `${day} · ${hr} WITA`;
}

// Swell|Wind exclusive video; Bathy + Sat are independent translucent overlays.
// Pinch / wheel zoom (1–4×) with pan when zoomed; spot dots transform with content.
function RegionalMap({ doc, spots, bathyUrl }) {
  const variants = useMemo(() => doc?.variants || [], [doc]);
  const [variantKey, setVariantKey] = useState('all');
  useEffect(() => {
    if (variants.length && !variants.some((v) => v.key === variantKey)) setVariantKey(variants[0].key);
  }, [variants, variantKey]);
  const cur = variants.find((v) => v.key === variantKey) || variants[0];

  const hasWind = !!doc?.windVideo;
  const satUrl = doc?.satUrl || '';
  const [layer, setLayer] = useState('swell');
  const isWind = layer === 'wind' && hasWind;
  const [showBathy, setShowBathy] = useState(false);
  const [showSat, setShowSat] = useState(false);
  // Esri World Imagery underlay — auto-on when pinch-zoomed; user can override.
  const [showBase, setShowBase] = useState(false);
  const [basePinned, setBasePinned] = useState(false);
  const [bathyOpacity, setBathyOpacity] = useState(BATHY_OPACITY_DEFAULT);
  const [satOpacity, setSatOpacity] = useState(SAT_OPACITY_DEFAULT);
  const url = isWind ? doc?.windVideo : cur?.url || '';
  const poster = isWind ? doc?.windPosterUrl : doc?.posterUrl;

  const [speed, setSpeed] = useState(1);
  const [playing, setPlaying] = useState(true);
  const [frac, setFrac] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const videoRef = useRef(null);
  const stageRef = useRef(null);
  const posRef = useRef(0);
  const zoomRef = useRef({ zoom: 1, pan: { x: 0, y: 0 } });
  zoomRef.current = { zoom, pan };

  useEffect(() => {
    if (basePinned) return;
    setShowBase(zoom >= 1.55);
  }, [zoom, basePinned]);

  const nowFrac = doc?.nowFrac || 0;
  const startTime = doc?.startTime;
  const endTime = doc?.endTime;

  // Open at "now" and preserve the playhead across variant/layer swaps.
  useEffect(() => { posRef.current = nowFrac; setFrac(nowFrac); }, [nowFrac]);
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !url) return undefined;
    const target = posRef.current;
    const wasPlaying = playing;
    v.src = url;
    v.load();
    const onMeta = () => {
      if (v.duration && Number.isFinite(v.duration)) {
        v.currentTime = Math.min(v.duration - 0.05, Math.max(0, target * v.duration));
      }
      v.playbackRate = speed;
      if (wasPlaying) v.play().catch(() => {});
    };
    v.addEventListener('loadedmetadata', onMeta, { once: true });
    return () => v.removeEventListener('loadedmetadata', onMeta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);
  useEffect(() => {
    const v = videoRef.current;
    if (v) v.playbackRate = speed;
  }, [speed]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play().catch(() => {}); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };
  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    const f = v.currentTime / v.duration;
    posRef.current = f;
    setFrac(f);
  };
  const seekFrac = (f) => {
    const v = videoRef.current;
    posRef.current = f;
    setFrac(f);
    if (v && v.duration) v.currentTime = Math.min(v.duration - 0.05, Math.max(0, f * v.duration));
  };

  const clampPan = (z, p, w, h) => {
    if (z <= 1.001) return { x: 0, y: 0 };
    const maxX = (w / 2) * (z - 1) + 8;
    const maxY = (h / 2) * (z - 1) + 8;
    return {
      x: Math.max(-maxX, Math.min(maxX, p.x)),
      y: Math.max(-maxY, Math.min(maxY, p.y)),
    };
  };
  const applyZoomPan = (z, p) => {
    const el = stageRef.current;
    const w = el?.clientWidth || 1;
    const h = el?.clientHeight || 1;
    const zz = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));
    const pp = clampPan(zz, p, w, h);
    zoomRef.current = { zoom: zz, pan: pp };
    setZoom(zz);
    setPan(pp);
  };
  const resetZoom = () => applyZoomPan(1, { x: 0, y: 0 });

  // Pinch + wheel zoom / pan; one-finger drag when zoomed; double-tap resets.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;

    let pinch = null;
    let drag = null;
    let lastTapAt = 0;
    let moved = false;

    const midOf = (t0, t1) => ({
      x: (t0.clientX + t1.clientX) / 2,
      y: (t0.clientY + t1.clientY) / 2,
    });
    const distOf = (t0, t1) => {
      const dx = t0.clientX - t1.clientX;
      const dy = t0.clientY - t1.clientY;
      return Math.hypot(dx, dy) || 1;
    };
    const relCenter = (clientX, clientY) => {
      const r = el.getBoundingClientRect();
      return { x: clientX - r.left - r.width / 2, y: clientY - r.top - r.height / 2 };
    };

    const onWheel = (e) => {
      e.preventDefault();
      const { zoom: z0, pan: p0 } = zoomRef.current;
      const m = relCenter(e.clientX, e.clientY);
      const factor = Math.exp(-e.deltaY * 0.0015);
      const z1 = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z0 * factor));
      if (Math.abs(z1 - z0) < 1e-6) return;
      const t = z1 / z0;
      applyZoomPan(z1, { x: m.x - t * (m.x - p0.x), y: m.y - t * (m.y - p0.y) });
    };

    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        drag = null;
        const { zoom: z0, pan: p0 } = zoomRef.current;
        const mid = midOf(e.touches[0], e.touches[1]);
        pinch = {
          dist0: distOf(e.touches[0], e.touches[1]),
          zoom0: z0,
          mid0: relCenter(mid.x, mid.y),
          pan0: { ...p0 },
        };
        return;
      }
      if (e.touches.length === 1 && zoomRef.current.zoom > 1.001) {
        const t = e.touches[0];
        drag = { x0: t.clientX, y0: t.clientY, pan0: { ...zoomRef.current.pan } };
        moved = false;
      }
    };
    const onTouchMove = (e) => {
      if (pinch && e.touches.length >= 2) {
        e.preventDefault();
        const mid = midOf(e.touches[0], e.touches[1]);
        const midNow = relCenter(mid.x, mid.y);
        const scale = distOf(e.touches[0], e.touches[1]) / pinch.dist0;
        const z1 = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, pinch.zoom0 * scale));
        // Keep the pinch midpoint's content point under the fingers as zoom/pan change.
        const panX = midNow.x - (z1 / pinch.zoom0) * (pinch.mid0.x - pinch.pan0.x);
        const panY = midNow.y - (z1 / pinch.zoom0) * (pinch.mid0.y - pinch.pan0.y);
        applyZoomPan(z1, { x: panX, y: panY });
        return;
      }
      if (drag && e.touches.length === 1) {
        e.preventDefault();
        const t = e.touches[0];
        const dx = t.clientX - drag.x0;
        const dy = t.clientY - drag.y0;
        if (Math.hypot(dx, dy) > 4) moved = true;
        applyZoomPan(zoomRef.current.zoom, { x: drag.pan0.x + dx, y: drag.pan0.y + dy });
      }
    };
    const onTouchEnd = (e) => {
      if (e.touches.length < 2) pinch = null;
      if (e.touches.length === 0) {
        if (!moved) {
          const now = Date.now();
          if (now - lastTapAt < 320) {
            resetZoom();
            lastTapAt = 0;
          } else {
            lastTapAt = now;
          }
        }
        drag = null;
        moved = false;
      }
    };

    let ptrDrag = null;
    const onPtrDown = (e) => {
      if (e.pointerType === 'touch') return;
      if (zoomRef.current.zoom <= 1.001) return;
      ptrDrag = {
        id: e.pointerId,
        x0: e.clientX,
        y0: e.clientY,
        pan0: { ...zoomRef.current.pan },
        moved: false,
      };
      try { el.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    };
    const onPtrMove = (e) => {
      if (!ptrDrag || e.pointerId !== ptrDrag.id) return;
      const dx = e.clientX - ptrDrag.x0;
      const dy = e.clientY - ptrDrag.y0;
      if (Math.hypot(dx, dy) > 4) ptrDrag.moved = true;
      applyZoomPan(zoomRef.current.zoom, { x: ptrDrag.pan0.x + dx, y: ptrDrag.pan0.y + dy });
    };
    const onPtrUp = (e) => {
      if (!ptrDrag || e.pointerId !== ptrDrag.id) return;
      const wasDrag = ptrDrag.moved;
      ptrDrag = null;
      if (!wasDrag) {
        const now = Date.now();
        if (now - lastTapAt < 320) {
          resetZoom();
          lastTapAt = 0;
        } else lastTapAt = now;
      }
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchcancel', onTouchEnd);
    el.addEventListener('pointerdown', onPtrDown);
    el.addEventListener('pointermove', onPtrMove);
    el.addEventListener('pointerup', onPtrUp);
    el.addEventListener('pointercancel', onPtrUp);
    el.addEventListener('dblclick', (e) => { e.preventDefault(); resetZoom(); });

    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
      el.removeEventListener('pointerdown', onPtrDown);
      el.removeEventListener('pointermove', onPtrMove);
      el.removeEventListener('pointerup', onPtrUp);
      el.removeEventListener('pointercancel', onPtrUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!doc?.video?.all]);

  // Spot dots over the letterboxed video (object-fit: contain math).
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = stageRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const update = () => setStageSize({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const placed = useMemo(() => {
    if (!doc || !stageSize.w || !stageSize.h) return [];
    const VW = doc.width;
    const VH = doc.height;
    if (!Number.isFinite(VW) || !Number.isFinite(VH)) return [];
    const scale = Math.min(stageSize.w / VW, stageSize.h / VH);
    const dispW = VW * scale;
    const dispH = VH * scale;
    const offX = (stageSize.w - dispW) / 2;
    const offY = (stageSize.h - dispH) / 2;
    return (spots || [])
      .map((spot) => {
        const p = projectSpot(spot, doc);
        return p ? { spot, x: offX + p.xFrac * dispW, y: offY + p.yFrac * dispH } : null;
      })
      .filter(Boolean);
  }, [doc, stageSize, spots]);

  const curSec = startTime && endTime ? startTime + frac * (endTime - startTime) : null;
  const isForecast = frac > nowFrac + 1e-4;
  const zoomed = zoom > 1.001;

  if (!doc?.video?.all) {
    return (
      <div className="cs-rote-map cs-rote-map-empty">
        Regional swell map is rendering — the worker builds it on its next cycle (a few minutes
        after deploy, then twice daily). The strike calendar below works right away.
      </div>
    );
  }

  return (
    <div className="cs-rote-map">
      <div
        className={`cs-rote-map-stage${zoomed ? ' is-zoomed' : ''}`}
        ref={stageRef}
        title="Pinch or scroll to zoom · drag to pan · double-tap to reset"
      >
        <div
          className="cs-rote-map-zoom"
          style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}
        >
          {showBase && stageSize.w > 0 && (
            <BasinImageryTiles
              doc={doc}
              stageW={stageSize.w}
              stageH={stageSize.h}
              mapZoom={zoom}
              opacity={1}
            />
          )}
          <video
            ref={videoRef}
            className={`cs-rote-map-video${showBase ? ' is-over-base' : ''}`}
            style={showBase ? { opacity: 0.58 } : undefined}
            poster={poster || undefined}
            muted
            loop
            playsInline
            autoPlay
            preload="auto"
            onTimeUpdate={onTimeUpdate}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onClick={(e) => {
              if (zoomed) return;
              e.preventDefault();
              togglePlay();
            }}
          />
          {showSat && satUrl && (
            <img
              className="cs-rote-map-overlay"
              src={satUrl}
              alt=""
              draggable={false}
              style={{ opacity: satOpacity }}
            />
          )}
          {showBathy && bathyUrl && (
            <img
              className="cs-rote-map-overlay"
              src={bathyUrl}
              alt=""
              draggable={false}
              style={{ opacity: bathyOpacity }}
            />
          )}
          {placed.map(({ spot, x, y }) => (
            <span key={spot.id} className="cs-rote-map-spot" style={{ left: `${x}px`, top: `${y}px` }}>
              <i />
              <em>{spot.name}</em>
            </span>
          ))}
        </div>
        {zoomed && (
          <button type="button" className="cs-rote-map-resetzoom" onClick={resetZoom} title="Reset zoom">
            Reset · {zoom.toFixed(1)}×
          </button>
        )}
      </div>
      <div className="cs-rote-map-bar">
        {/* Transport row stays one line — pinned time width so the scrub never jitters. */}
        <div className="cs-rote-map-transport">
          <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={togglePlay} title={playing ? 'Pause' : 'Play'}>
            {playing ? '⏸' : '▶'}
          </button>
          <span className="cs-rote-map-scrub">
            <input type="range" min={0} max={1} step={0.0005} value={frac} onChange={(e) => seekFrac(Number(e.target.value))} />
            <span className="cs-rote-map-nowtick" style={{ left: `${nowFrac * 100}%` }} title="Now" />
          </span>
          <button
            className={`cs-btn cs-btn-ghost cs-btn-sm ${Math.abs(frac - nowFrac) < 0.004 ? 'is-on' : ''}`}
            onClick={() => seekFrac(nowFrac)}
          >
            Now
          </button>
          <span className="cs-rote-map-time" aria-live="off">
            <span className="cs-rote-map-timetext">{curSec ? frameTimeLabel(curSec) : '\u00a0'}</span>
            <em className={curSec ? (isForecast ? 'fc' : 'hist') : 'idle'}>
              {curSec ? (isForecast ? 'forecast' : 'history') : '\u00a0'}
            </em>
          </span>
        </div>
        <div className="cs-rote-map-opts">
          <span className="cs-sm-seg">
            <button className={layer === 'swell' ? 'on' : ''} onClick={() => setLayer('swell')}>Swell</button>
            {hasWind && <button className={isWind ? 'on' : ''} onClick={() => setLayer('wind')}>Wind</button>}
          </span>
          <span className="cs-sm-seg">
            <button
              type="button"
              className={showBase ? 'on' : ''}
              onClick={() => {
                setBasePinned(true);
                setShowBase((v) => !v);
              }}
              title="Esri World Imagery underlay — reef-scale detail when zoomed (© Esri / Maxar)"
            >
              Base
            </button>
            {bathyUrl && (
              <button
                type="button"
                className={showBathy ? 'on' : ''}
                onClick={() => setShowBathy((v) => !v)}
                title="GEBCO seafloor depth — reefs and shelf edges (translucent overlay)"
              >
                Bathy
              </button>
            )}
            {satUrl && (
              <button
                type="button"
                className={showSat ? 'on' : ''}
                onClick={() => setShowSat((v) => !v)}
                title="NASA GIBS satellite still for this basin (translucent overlay)"
              >
                Sat
              </button>
            )}
          </span>
          {showBathy && bathyUrl && (
            <label className="cs-rote-map-op" title="Bathymetry opacity">
              <span>Bathy</span>
              <input
                type="range"
                min={0.15}
                max={0.85}
                step={0.05}
                value={bathyOpacity}
                onChange={(e) => setBathyOpacity(Number(e.target.value))}
              />
            </label>
          )}
          {showSat && satUrl && (
            <label className="cs-rote-map-op" title="Satellite opacity">
              <span>Sat</span>
              <input
                type="range"
                min={0.15}
                max={0.85}
                step={0.05}
                value={satOpacity}
                onChange={(e) => setSatOpacity(Number(e.target.value))}
              />
            </label>
          )}
          {layer === 'swell' && variants.length > 1 && (
            <span className="cs-sm-seg">
              {variants.map((v) => (
                <button key={v.key} className={v.key === variantKey ? 'on' : ''} onClick={() => setVariantKey(v.key)}>
                  {v.label}
                </button>
              ))}
            </span>
          )}
          <span className="cs-sm-seg">
            {SPEEDS.map((s) => (
              <button key={s} className={s === speed ? 'on' : ''} onClick={() => setSpeed(s)}>{s}×</button>
            ))}
          </span>
        </div>
      </div>
    </div>
  );
}

// ------------------------- per-spot preference chips -------------------------
function arcLabel(arc) {
  return arc ? `${compass16(arc.from)}–${compass16(arc.to)} (${Math.round(arc.from)}–${Math.round(arc.to)}°)` : null;
}

function PrefChips({ spot }) {
  const strike = strikeFor(spot);
  const beachFacing = Number.isFinite(spot.beachFacing) ? spot.beachFacing : null;
  const swellArc = resolveSwellArc(strike);
  const windArc = resolveWindArc(strike, beachFacing);
  const band = tideBand(spot.tide);
  const tideLbl = spot.tide?.mode === 'custom' && band
    ? `${Math.round(band.lo * 100)}–${Math.round(band.hi * 100)}%`
    : TIDE_MODE_LABEL[spot.tide?.mode || 'any'];
  return (
    <div className="cs-rote-prefs">
      <span className="cs-fc-chip" title="Swell directions this spot lights up on">
        <em>Swell</em>
        <b>{arcLabel(swellArc) || 'any direction'}</b>
      </span>
      <span className="cs-fc-chip" title="Tolerable wind: direction arc + max speed">
        <em>Wind</em>
        <b>
          {arcLabel(windArc) || 'any'}
          {strike.maxWindKn != null ? ` · ≤${strike.maxWindKn}kn` : ''}
        </b>
      </span>
      <span className="cs-fc-chip" title="Working tide band">
        <em>Tide</em>
        <b>{tideLbl}</b>
      </span>
      <span className="cs-fc-chip" title="The face height that counts as epic here">
        <em>Epic ≥</em>
        <b>{strike.minSwellFt != null ? `${strike.minSwellFt}ft` : '—'}{strike.minPeriodS != null ? ` @ ${strike.minPeriodS}s+` : ''}</b>
      </span>
    </div>
  );
}

// --------------------------- strike calendar strip ---------------------------
// One row of 16 day-cells: background graded by that day's peak face height,
// 🔥 ring when a strike window touches the day, wind-quality dot and swell
// arrow at the daily peak. Hover a cell for the full readout.
function dayGrade(peakFt, epic, good) {
  if (epic) return 'epic';
  if (good) return 'good';
  if (peakFt == null || peakFt < 2) return 'flat';
  return 'some';
}

function buildDays(spot, cond) {
  const calib = spot.calibration?.enabled ? spot.calibration : null;
  const beachFacing = Number.isFinite(spot.beachFacing) ? spot.beachFacing : null;
  const strike = strikeFor(spot);
  const stEval = strike.enabled !== false
    ? evaluateStrike(cond, strike, beachFacing, calib, spot.tide)
    : { windows: [] };
  const alEval = evaluateAlerts(cond, spot.thresholds, calib, spot.tide);
  const nowSec = Date.now() / 1000;

  return groupByDay(cond)
    .filter((d) => d.hours[d.hours.length - 1].t >= nowSec - 3600)
    .slice(0, FORECAST_DAYS)
    .map((d) => {
      let peak = null;
      d.hours.forEach((h) => {
        const ft = surfFaceFt(h, calib);
        if (ft == null) return;
        if (!peak || ft > peak.ft) {
          const cs = combinedSurf(h, calib);
          peak = {
            ft,
            rawFt: cs.hs,
            period: hourPeriodS(h),
            dir: hourSwellDir(h),
            windKn: hourWindKn(h),
            windDir: h.windDir,
            t: h.t,
          };
        }
      });
      const t0 = d.hours[0].t;
      const t1 = d.hours[d.hours.length - 1].t;
      const overlaps = (w) => w.endSec >= t0 && w.startSec <= t1;
      const epicWin = stEval.windows.find(overlaps) || null;
      const good = (alEval.windows || []).some(overlaps);
      const wq = peak && peak.windDir != null ? windQuality(peak.windDir, beachFacing, peak.windKn) : null;
      return { day: d, peak, epicWin, good, wq, grade: dayGrade(peak?.ft, !!epicWin, good) };
    });
}

// Day-cell background: size → hue, grade → intensity, vertical gradient so
// strike / alert days read hotter than flat ones on the per-spot strip.
function cellStyle(cell) {
  const ft = cell.peak?.ft;
  if (ft == null || ft < 1) return {};
  const col = swellColor(ft);
  const boost = cell.epicWin ? 1.15 : cell.good ? 1.0 : 0.72;
  const aTop = Math.min(0.92, (0.2 + ft / 16) * boost);
  const aBot = Math.min(0.7, aTop * 0.45);
  return {
    background: `linear-gradient(180deg, color-mix(in srgb, ${col} ${Math.round(aTop * 100)}%, transparent) 0%, color-mix(in srgb, ${col} ${Math.round(aBot * 100)}%, transparent) 100%)`,
  };
}

function StrikeStrip({ spot, cond }) {
  const cells = useMemo(() => buildDays(spot, cond), [spot, cond]);
  const [tip, setTip] = useState(null); // { idx, x, y }
  const active = tip != null ? cells[tip.idx] : null;

  const off = (cond.utcOffsetSec || 0) * 1000;
  const dayLbl = (d) => {
    const ld = new Date(d.hours[0].t * 1000 + off);
    return {
      dow: ld.toLocaleDateString(undefined, { weekday: 'short', timeZone: 'UTC' }),
      dom: ld.getUTCDate(),
    };
  };
  const hourLbl = (t) => {
    const ld = new Date(t * 1000 + off);
    return ld.toLocaleTimeString([], { hour: 'numeric', timeZone: 'UTC' });
  };

  return (
    <div className="cs-rote-strip" onMouseLeave={() => setTip(null)}>
      {cells.map((c, i) => {
        const { dow, dom } = dayLbl(c.day);
        return (
          <button
            key={c.day.key}
            type="button"
            className={`cs-rote-cell is-${c.grade} ${i >= CONFIDENCE_DAYS ? 'is-ext' : ''}`}
            style={cellStyle(c)}
            onMouseEnter={(e) => setTip({ idx: i, x: e.clientX, y: e.clientY })}
            onMouseMove={(e) => setTip({ idx: i, x: e.clientX, y: e.clientY })}
            onFocus={() => setTip(null)}
          >
            <span className="cs-rote-cell-day">{dow}</span>
            <span className="cs-rote-cell-dom">{dom}</span>
            <span className="cs-rote-cell-ft">{c.peak?.ft != null ? Math.round(c.peak.ft) : '·'}</span>
            {c.epicWin && <span className="cs-rote-cell-fire">🔥</span>}
            <span className="cs-rote-cell-meta">
              {c.peak?.dir != null && (
                <span className="cs-fc-arrow sm" style={{ transform: `rotate(${arrowRotation(c.peak.dir)}deg)` }}>↑</span>
              )}
              {c.wq && <i className="cs-rote-cell-wind" style={{ background: WIND_QUALITY_COLOR[c.wq] }} />}
            </span>
          </button>
        );
      })}
      {active && (
        <div
          className="cs-gantt-tip"
          style={{
            left: `${Math.max(96, Math.min((typeof window !== 'undefined' ? window.innerWidth : 1200) - 96, tip.x))}px`,
            top: `${tip.y}px`,
          }}
        >
          <div className="cs-gantt-tip-time">
            {spot.name} · {dayLbl(active.day).dow} {dayLbl(active.day).dom}
            {active.epicWin ? ' · 🔥 strike window' : active.good ? ' · meets alert bar' : ''}
          </div>
          {active.peak ? (
            <>
              <div className="cs-meteo-tip-row">
                <span className="cs-meteo-tip-k">Peak</span>
                <span className="cs-meteo-tip-v" style={{ color: swellColor(active.peak.ft) }}>
                  {fmtFt(active.peak.ft)} face @ {hourLbl(active.peak.t)}
                </span>
              </div>
              <div className="cs-meteo-tip-row">
                <span className="cs-meteo-tip-k">Swell</span>
                <span className="cs-meteo-tip-v">
                  {fmtFt(active.peak.rawFt)}{active.peak.period != null ? ` @ ${fmtPeriod(active.peak.period)}` : ''}
                  {active.peak.dir != null ? ` ${dirLabel(active.peak.dir)}` : ''}
                </span>
              </div>
              <div className="cs-meteo-tip-row">
                <span className="cs-meteo-tip-k">Wind</span>
                <span className="cs-meteo-tip-v">
                  <i style={{ background: (active.wq && WIND_QUALITY_COLOR[active.wq]) || '#9aa0a6' }} />
                  {fmtKn(active.peak.windKn)}
                  {active.peak.windDir != null ? ` ${dirLabel(active.peak.windDir, false)}` : ''}
                  {active.wq ? ` · ${WIND_QUALITY_LABEL[active.wq]}` : ''}
                </span>
              </div>
              {active.epicWin && (
                <div className="cs-meteo-tip-row">
                  <span className="cs-meteo-tip-k">Window</span>
                  <span className="cs-meteo-tip-v">
                    {hourLbl(active.epicWin.startSec)}–{hourLbl(active.epicWin.endSec)} · up to {fmtFt(active.epicWin.peakFt)}
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="cs-meteo-tip-row"><span className="cs-meteo-tip-v">No swell data this day</span></div>
          )}
        </div>
      )}
    </div>
  );
}

// ----------------------- today / tomorrow hourly detail -----------------------
// Two day cards per spot: hour-by-hour surf bars + tide curve + wind/swell rows,
// with the day's BEST windows highlighted. "Best" is scored per hour as
// size × wind-quality × tide-band × swell-angle (daylight only), then the
// contiguous hours near the day's peak score become the starred window(s) —
// so even a modest day still shows when to paddle out.

const hourScore = (h, spot, strike, calib, daylight) => {
  if (!daylight) return 0;
  const ft = surfFaceFt(h, calib);
  if (ft == null || ft <= 0) return 0;
  const bar = strike.minSwellFt || 4;
  const size = Math.min(1, ft / bar);
  const kn = hourWindKn(h);
  const wq = windQuality(h.windDir, Number.isFinite(spot.beachFacing) ? spot.beachFacing : null, kn);
  let windF = 0.8; // unknown wind: neutral-ish
  if (kn != null && strike.maxWindKn != null && kn > strike.maxWindKn) windF = 0.15;
  else if (wq === 'glass' || wq === 'offshore') windF = 1;
  else if (wq === 'cross') windF = 0.6;
  else if (wq === 'onshore') windF = 0.3;
  const tideF = tideOk(h, spot.tide) ? 1 : 0.35;
  const dirF = swellDirOk(h, strike) ? 1 : 0.4;
  return size * windF * tideF * dirF;
};

// Contiguous runs of hours whose score rides near the day's peak.
function bestWindows(slots) {
  const max = slots.reduce((m, s) => Math.max(m, s.score), 0);
  if (max < 0.12) return []; // nothing worth paddling for
  const thr = Math.max(max * 0.75, 0.1);
  const wins = [];
  let cur = null;
  slots.forEach((s, i) => {
    if (s.score >= thr) {
      if (cur && i === cur.endIdx + 1) cur.endIdx = i;
      else { cur = { startIdx: i, endIdx: i }; wins.push(cur); }
    } else cur = null;
  });
  // Longest / strongest first, keep at most two.
  wins.forEach((w) => {
    w.peak = 0;
    for (let i = w.startIdx; i <= w.endIdx; i += 1) w.peak = Math.max(w.peak, slots[i].score);
  });
  wins.sort((a, b) => (b.endIdx - b.startIdx) - (a.endIdx - a.startIdx) || b.peak - a.peak);
  return wins.slice(0, 2).sort((a, b) => a.startIdx - b.startIdx);
}

function DayCard({ spot, cond, day, label, ftMax }) {
  const [tip, setTip] = useState(null); // { idx, x, y }
  const calib = spot.calibration?.enabled ? spot.calibration : null;
  const beachFacing = Number.isFinite(spot.beachFacing) ? spot.beachFacing : null;
  const strike = strikeFor(spot);
  const off = (cond.utcOffsetSec || 0) * 1000;
  const nowSec = Date.now() / 1000;

  const hourLbl = (t, withM = false) => {
    const ld = new Date(t * 1000 + off);
    return ld.toLocaleTimeString([], withM
      ? { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' }
      : { hour: 'numeric', timeZone: 'UTC' });
  };

  // Daylight bounds for this local day (from the daily sunrise/sunset arrays).
  const t0 = day.hours[0].t;
  const t1 = day.hours[day.hours.length - 1].t;
  const sunrise = (cond.daily?.sunrise || []).find((s) => s >= t0 && s <= t1) ?? null;
  const sunset = (cond.daily?.sunset || []).find((s) => s >= t0 && s <= t1) ?? null;
  const isDaylight = (t) => (sunrise == null || t >= sunrise - 1800) && (sunset == null || t <= sunset + 1800);

  const slots = useMemo(() => day.hours.map((h) => {
    const ft = surfFaceFt(h, calib);
    const cs = combinedSurf(h, calib);
    const kn = hourWindKn(h);
    const wq = windQuality(h.windDir, beachFacing, kn);
    return {
      h,
      ft,
      rawFt: cs.hs,
      period: hourPeriodS(h),
      dir: hourSwellDir(h),
      kn,
      wq,
      score: hourScore(h, spot, strike, calib, isDaylight(h.t)),
      past: h.t < nowSec - 1800,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [day, spot]);

  const wins = useMemo(() => bestWindows(slots), [slots]);
  const winLbl = (w) => `${hourLbl(slots[w.startIdx].h.t)}–${hourLbl(slots[w.endIdx].h.t + 3600)}`;

  // --- SVG geometry: surf bars + tide curve share one canvas ---
  const n = slots.length;
  const COL = 26;
  const W = n * COL;
  const H = 128;
  const barTop = 12;
  const barBottom = 86; // bars live above the tide lane
  const tideTop = 88;
  const tideBottom = 122;
  const xC = (i) => i * COL + COL / 2;
  const yFt = (ft) => barBottom - Math.max(0, Math.min(1, ft / ftMax)) * (barBottom - barTop);

  const tideVals = slots.map((s) => s.h.tideM).filter((v) => v != null);
  const tLo = tideVals.length ? Math.min(...tideVals) : 0;
  const tHi = tideVals.length ? Math.max(...tideVals) : 1;
  const yTide = (v) => tideBottom - ((v - tLo) / (tHi - tLo || 1)) * (tideBottom - tideTop);
  const tidePath = slots
    .map((s, i) => (s.h.tideM != null ? `${i === 0 || slots[i - 1].h.tideM == null ? 'M' : 'L'}${xC(i)},${yTide(s.h.tideM)}` : ''))
    .join(' ');
  const extremes = (cond.tideExtremes || []).filter((e) => e.t >= t0 && e.t <= t1);
  const band = tideBand(spot.tide);

  const nowX = nowSec >= t0 && nowSec <= t1 + 3600 ? ((nowSec - t0) / 3600) * COL + COL / 2 : null;
  const active = tip != null ? slots[tip.idx] : null;

  return (
    <div className="cs-rote-day" onMouseLeave={() => setTip(null)}>
      <div className="cs-rote-day-head">
        <b>{label}</b>
        <span className="cs-rote-day-date">{day.hours.length ? new Date(t0 * 1000 + off).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }) : ''}</span>
        {wins.length ? (
          wins.map((w, i) => (
            <span key={i} className="cs-rote-day-best" title="Best window: size + wind + tide + swell angle, daylight only">
              ★ {winLbl(w)}
            </span>
          ))
        ) : (
          <span className="cs-rote-day-none">no window worth it</span>
        )}
        {sunrise != null && sunset != null && (
          <span className="cs-rote-day-sun" title="Sunrise – sunset">
            ☀ {hourLbl(sunrise, true)}–{hourLbl(sunset, true)}
          </span>
        )}
      </div>

      <div className="cs-rote-day-scroll">
        <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} className="cs-rote-day-svg">
          {/* Night shading */}
          {sunrise != null && sunrise > t0 && (
            <rect x={0} y={0} width={((sunrise - t0) / 3600) * COL} height={H} className="cs-rote-day-night" />
          )}
          {sunset != null && sunset < t1 + 3600 && (
            <rect x={((sunset - t0) / 3600) * COL} y={0} width={W - ((sunset - t0) / 3600) * COL} height={H} className="cs-rote-day-night" />
          )}

          {/* Best-window highlight bands */}
          {wins.map((w, i) => (
            <rect
              key={i}
              x={w.startIdx * COL}
              y={0}
              width={(w.endIdx - w.startIdx + 1) * COL}
              height={H}
              className="cs-rote-day-bestband"
            />
          ))}

          {/* Preferred tide band (over the tide lane only) */}
          {band && tideVals.length > 0 && (
            <rect
              x={0}
              y={tideBottom - band.hi * (tideBottom - tideTop)}
              width={W}
              height={Math.max(1, (band.hi - band.lo) * (tideBottom - tideTop))}
              className="cs-rote-day-tideband"
            />
          )}

          {/* Surf bars — opacity tracks score vs the day's peak (best hours glow). */}
          {(() => {
            const peakSc = slots.reduce((m, s) => Math.max(m, s.score || 0), 0) || 1;
            return slots.map((s, i) => {
              if (s.ft == null || s.ft <= 0.05) return null;
              const rel = Math.max(0, (s.score || 0) / peakSc);
              const inWin = wins.some((w) => i >= w.startIdx && i <= w.endIdx);
              const op = s.past ? 0.28 : (0.22 + 0.78 * (rel ** 0.75)) * (inWin ? 1 : 0.85);
              return (
                <rect
                  key={i}
                  x={xC(i) - 8}
                  y={yFt(s.ft)}
                  width={16}
                  height={Math.max(1.5, barBottom - yFt(s.ft))}
                  rx={2}
                  fill={swellColor(s.ft)}
                  opacity={op}
                />
              );
            });
          })()}
          {/* ft labels every 3h on daylight bars */}
          {slots.map((s, i) => (
            i % 3 === 1 && s.ft != null && s.ft > 0.3 ? (
              <text key={i} x={xC(i)} y={yFt(s.ft) - 3} textAnchor="middle" className="cs-rote-day-ftlbl">
                {s.ft >= 9.5 ? Math.round(s.ft) : s.ft.toFixed(1)}
              </text>
            ) : null
          ))}

          {/* Tide lane divider + curve + extremes */}
          <line x1={0} y1={tideTop - 2} x2={W} y2={tideTop - 2} className="cs-rote-day-divider" />
          {tidePath && <path d={tidePath} className="cs-rote-day-tide" />}
          {extremes.map((e, i) => {
            const x = ((e.t - t0) / 3600) * COL + COL / 2;
            if (x < 6 || x > W - 6) return null;
            return (
              <g key={i}>
                <circle cx={x} cy={yTide(e.heightM)} r={2.6} className="cs-rote-day-tidedot" />
                <text x={x} y={e.type === 'high' ? yTide(e.heightM) - 5 : Math.min(H - 2, yTide(e.heightM) + 11)} textAnchor="middle" className="cs-rote-day-tidelbl">
                  {e.type === 'high' ? 'H' : 'L'} {hourLbl(e.t, true)} · {e.heightM}m
                </text>
              </g>
            );
          })}

          {/* Now marker */}
          {nowX != null && (
            <g>
              <line x1={nowX} y1={0} x2={nowX} y2={H} className="cs-rote-day-now" />
            </g>
          )}

          {/* Hover targets */}
          {slots.map((s, i) => (
            <rect
              key={i}
              x={i * COL}
              y={0}
              width={COL}
              height={H}
              fill="transparent"
              onMouseEnter={(e) => setTip({ idx: i, x: e.clientX, y: e.clientY })}
              onMouseMove={(e) => setTip({ idx: i, x: e.clientX, y: e.clientY })}
            />
          ))}
        </svg>

        {/* Wind row: arrow colored by quality, speed every 2h */}
        <div className="cs-rote-day-row" style={{ width: `${W}px` }}>
          {slots.map((s, i) => (
            <span key={i} className={`cs-rote-day-cellrow ${s.past ? 'is-past' : ''}`} style={{ width: `${COL}px` }}>
              {s.kn != null && s.h.windDir != null ? (
                <i
                  className="cs-rote-day-warrow"
                  style={{ transform: `rotate(${arrowRotation(s.h.windDir)}deg)`, color: (s.wq && WIND_QUALITY_COLOR[s.wq]) || '#9aa0a6' }}
                >
                  ↑
                </i>
              ) : <i className="cs-rote-day-warrow">·</i>}
              {i % 2 === 0 && s.kn != null ? <em>{Math.round(s.kn)}</em> : <em />}
            </span>
          ))}
        </div>

        {/* Swell row: direction arrow + period every 3h */}
        <div className="cs-rote-day-row cs-rote-day-swellrow" style={{ width: `${W}px` }}>
          {slots.map((s, i) => (
            <span key={i} className={`cs-rote-day-cellrow ${s.past ? 'is-past' : ''}`} style={{ width: `${COL}px` }}>
              {i % 3 === 1 && s.dir != null ? (
                <>
                  <i className="cs-rote-day-warrow" style={{ transform: `rotate(${arrowRotation(s.dir)}deg)`, color: '#7fa8d9' }}>↑</i>
                  <em>{s.period != null ? `${Math.round(s.period)}s` : ''}</em>
                </>
              ) : <em />}
            </span>
          ))}
        </div>

        {/* Hour ticks */}
        <div className="cs-rote-day-row cs-rote-day-hours" style={{ width: `${W}px` }}>
          {slots.map((s, i) => (
            <span key={i} style={{ width: `${COL}px` }}>{i % 3 === 1 ? hourLbl(s.h.t) : ''}</span>
          ))}
        </div>
      </div>

      {active && (
        <div
          className="cs-gantt-tip"
          style={{
            left: `${Math.max(96, Math.min((typeof window !== 'undefined' ? window.innerWidth : 1200) - 96, tip.x))}px`,
            top: `${tip.y}px`,
          }}
        >
          <div className="cs-gantt-tip-time">
            {spot.name} · {hourLbl(active.h.t)}
            {active.score > 0 && wins.some((w) => tip.idx >= w.startIdx && tip.idx <= w.endIdx) ? ' · ★ best window' : ''}
          </div>
          <div className="cs-meteo-tip-row">
            <span className="cs-meteo-tip-k">Surf</span>
            <span className="cs-meteo-tip-v" style={{ color: swellColor(active.ft) }}>{fmtFt(active.ft)} face</span>
          </div>
          <div className="cs-meteo-tip-row">
            <span className="cs-meteo-tip-k">Swell</span>
            <span className="cs-meteo-tip-v">
              {fmtFt(active.rawFt)}{active.period != null ? ` @ ${fmtPeriod(active.period)}` : ''}
              {active.dir != null ? ` ${dirLabel(active.dir)}` : ''}
            </span>
          </div>
          <div className="cs-meteo-tip-row">
            <span className="cs-meteo-tip-k">Wind</span>
            <span className="cs-meteo-tip-v">
              <i style={{ background: (active.wq && WIND_QUALITY_COLOR[active.wq]) || '#9aa0a6' }} />
              {fmtKn(active.kn)}
              {active.h.windDir != null ? ` ${dirLabel(active.h.windDir, false)}` : ''}
              {active.wq ? ` · ${WIND_QUALITY_LABEL[active.wq]}` : ''}
            </span>
          </div>
          <div className="cs-meteo-tip-row">
            <span className="cs-meteo-tip-k">Tide</span>
            <span className="cs-meteo-tip-v">
              {active.h.tideM != null ? `${active.h.tideM}m` : '—'}
              {active.h.tideStage01 != null ? ` · ${tideStageLabel(active.h.tideStage01)}` : ''}
              {active.h.tideState ? ` · ${active.h.tideState}` : ''}
              {!tideOk(active.h, spot.tide) ? ' · outside band' : ''}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function TwoDayPanel({ spot, cond }) {
  const days = useMemo(() => {
    const nowSec = Date.now() / 1000;
    return groupByDay(cond).filter((d) => d.hours[d.hours.length - 1].t >= nowSec - 3600).slice(0, 2);
  }, [cond]);
  const calib = spot.calibration?.enabled ? spot.calibration : null;
  // Shared bar scale across both days so today/tomorrow read against each other.
  const ftMax = useMemo(() => {
    let m = 3;
    days.forEach((d) => d.hours.forEach((h) => {
      const ft = surfFaceFt(h, calib);
      if (ft != null && ft > m) m = ft;
    }));
    return m * 1.15;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, spot]);
  if (!days.length) return null;
  return (
    <div className="cs-rote-days">
      {days.map((d, i) => (
        <DayCard key={d.key} spot={spot} cond={cond} day={d} label={i === 0 ? 'Today' : 'Tomorrow'} ftMax={ftMax} />
      ))}
    </div>
  );
}

// ------------------------------ hourly board -------------------------------
// The two-day, hour-by-hour cross-spot planner: one row per spot, one column
// per daylight hour (today + tomorrow), each cell scored with the same
// size × wind × tide × swell-angle hour score the day cards use, and a
// "best spot" crown row picking the top call every hour.

const HB_START = 5; // local hour range shown (dawn patrol → evening glass)
const HB_END = 19;

function slotFromHour(h, spot, strike, calib, beachFacing, stEval, di, nowSec, interp = false) {
  const ft = surfFaceFt(h, calib);
  const kn = hourWindKn(h);
  return {
    h,
    di,
    ft,
    rawFt: combinedSurf(h, calib).hs,
    period: hourPeriodS(h),
    dir: hourSwellDir(h),
    kn,
    wq: windQuality(h.windDir, beachFacing, kn),
    score: hourScore(h, spot, strike, calib, true),
    fire: stEval.windows.some((w) => h.t >= w.startSec && h.t <= w.endSec),
    past: h.t < nowSec - 1800,
    interp,
  };
}

function HourlyBoard({ spots, fmap }) {
  const [tip, setTip] = useState(null); // { ri, ci, x, y }

  const { rows, cols } = useMemo(() => {
    const nowSec = Date.now() / 1000;
    const built = spots
      .map((spot) => {
        const raw = fmap[spot.id]?.cond;
        if (!raw?.hours?.length) return null;
        // Densify so provider holes (common on reef coords) don't blank the board.
        const hours = densifyForecastHours(raw.hours);
        const cond = { ...raw, hours };
        const calib = spot.calibration?.enabled ? spot.calibration : null;
        const beachFacing = Number.isFinite(spot.beachFacing) ? spot.beachFacing : null;
        const strike = strikeFor(spot);
        const stEval = strike.enabled !== false
          ? evaluateStrike(cond, strike, beachFacing, calib, spot.tide)
          : { windows: [] };
        const days = groupByDay(cond)
          .filter((d) => d.hours[d.hours.length - 1].t >= nowSec - 3600)
          .slice(0, 2);
        const off = (cond.utcOffsetSec || 0) * 1000;
        const byT = new Map();
        days.forEach((d, di) => d.hours.forEach((h) => {
          const localHour = new Date(h.t * 1000 + off).getUTCHours();
          if (localHour < HB_START || localHour > HB_END) return;
          byT.set(h.t, slotFromHour(h, spot, strike, calib, beachFacing, stEval, di, nowSec, !!h._interp));
        }));
        return { spot, cond, off, byT, strike, calib, beachFacing, stEval };
      })
      .filter(Boolean);

    if (!built.length) return { rows: [], cols: [] };

    // Shared columns = union of every spot's daylight hours (not just densest).
    const meta = new Map(); // t -> di
    built.forEach((r) => {
      r.byT.forEach((slot, t) => {
        if (!meta.has(t)) meta.set(t, slot.di);
      });
    });
    const ts = [...meta.keys()].sort((a, b) => a - b);

    // Fill each spot's missing union hours by sampling/interpolating its series.
    const rowsFilled = built.map((r) => {
      const byT = new Map(r.byT);
      ts.forEach((t) => {
        if (byT.has(t)) return;
        const localHour = new Date(t * 1000 + r.off).getUTCHours();
        if (localHour < HB_START || localHour > HB_END) return;
        const h = sampleAt(r.cond, t * 1000);
        if (!h || (h.swellH == null && h.waveH == null)) return;
        byT.set(t, slotFromHour(
          { ...h, t, _interp: true },
          r.spot, r.strike, r.calib, r.beachFacing, r.stEval, meta.get(t) ?? 0, nowSec, true,
        ));
      });
      return { ...r, byT };
    });

    const off = rowsFilled[0].off;
    const colsOut = ts.map((t, ci) => {
      const di = meta.get(t);
      const ld = new Date(t * 1000 + off);
      let hr = ld.getUTCHours();
      const ap = hr >= 12 ? 'p' : 'a';
      hr = hr % 12 || 12;
      return {
        t,
        ci,
        di,
        label: `${hr}${ap}`,
        dayLbl: ld.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }),
        now: nowSec >= t && nowSec < t + 3600,
        dayStart: ci === 0 || meta.get(ts[ci - 1]) !== di,
      };
    });
    return { rows: rowsFilled, cols: colsOut };
  }, [spots, fmap]);

  // Crown: relative winner every hour (score, then face). No absolute score gate —
  // otherwise scrap-scoring days leave the whole ★ row as dashes.
  const bestByCol = useMemo(() => cols.map((c) => {
    let best = null;
    rows.forEach((r, ri) => {
      const s = r.byT.get(c.t);
      if (!s || s.ft == null || s.ft < 0.5) return;
      if (
        !best
        || s.score > best.slot.score
        || (s.score === best.slot.score && s.ft > best.slot.ft)
      ) best = { ri, slot: s, spot: r.spot };
    });
    return best;
  }), [cols, rows]);

  // Column peaks for relative shading (score + face).
  const colPeak = useMemo(() => cols.map((c) => {
    let maxScore = 0;
    let maxFt = 0;
    rows.forEach((r) => {
      const s = r.byT.get(c.t);
      if (!s) return;
      if (s.score > maxScore) maxScore = s.score;
      if ((s.ft || 0) > maxFt) maxFt = s.ft || 0;
    });
    return { maxScore, maxFt };
  }), [cols, rows]);

  if (!rows.length || cols.length < 4) return null;

  const dayGroups = [];
  cols.forEach((c) => {
    const g = dayGroups[dayGroups.length - 1];
    if (g && g.di === c.di) g.span += 1;
    else dayGroups.push({ di: c.di, span: 1, label: c.dayLbl, today: c.di === 0 });
  });

  const gridCols = { gridTemplateColumns: `88px repeat(${cols.length}, minmax(48px, 1fr))` };
  // "The Bommie" → "Bommie", "Nemberala Beach" → "Nemberala"
  const shortName = (s) => {
    let n = (s.name || '').replace(/\s*\(.*\)$/, '').replace(/^The\s+/i, '').trim();
    const parts = n.split(/\s+/);
    if (parts[0] && parts[0].length >= 3) n = parts[0];
    return n.slice(0, 9);
  };
  const tideShort = (h) => {
    if (h?.tideStage01 == null) return '';
    if (h.tideStage01 < 0.33) return 'L';
    if (h.tideStage01 > 0.66) return 'H';
    return 'M';
  };
  const activeRow = tip ? rows[tip.ri] : null;
  const activeSlot = tip && activeRow ? activeRow.byT.get(cols[tip.ci]?.t) : null;

  return (
    <div className="cs-rote-mission cs-rote-hb-wrap" onMouseLeave={() => setTip(null)}>
      <div className="cs-rote-mission-head">
        <h2>Next two days — hour by hour</h2>
        <span className="cs-help">
          Per daylight hour: face ft + period + swell direction · wind-quality dot · tide L/M/H.
          Gradient = size × conditions, brightest in-column = best spot. ★ / gold = top call · 🔥 = strike.
        </span>
      </div>

      <div className="cs-rote-mb-scroll">
        <div className="cs-rote-mb cs-rote-hb" style={gridCols}>
          {/* day band */}
          <div className="cs-rote-mb-corner" />
          {dayGroups.map((g, i) => (
            <div key={i} className={`cs-rote-hb-dayband ${g.today ? 'is-today' : ''}`} style={{ gridColumn: `span ${g.span}` }}>
              {g.today ? 'Today' : 'Tomorrow'} · {g.label}
            </div>
          ))}

          {/* hour labels */}
          <div className="cs-rote-mb-corner" />
          {cols.map((c) => (
            <div key={c.t} className={`cs-rote-hb-hour ${c.now ? 'is-now' : ''} ${c.dayStart ? 'is-daystart' : ''}`}>
              {c.label}
            </div>
          ))}

          {/* crown row */}
          <div className="cs-rote-mb-spot cs-rote-mb-bestlbl">★ Best spot</div>
          {cols.map((c, i) => {
            const b = bestByCol[i];
            return (
              <div
                key={c.t}
                className={`cs-rote-hb-best ${b ? 'has-pick' : ''} ${c.dayStart ? 'is-daystart' : ''} ${c.now ? 'is-now' : ''}`}
                title={b ? `${b.spot.name} · ${Math.round(b.slot.ft)}′ · ${sessionQ(b.slot.score)}` : ''}
              >
                {b ? (
                  <>
                    <b className={`q-${sessionQ(b.slot.score)}`}>{shortName(b.spot)}</b>
                    <span className="cs-rote-hb-bestft">{Math.round(b.slot.ft)}′</span>
                  </>
                ) : (
                  <span className="cs-rote-mb-skip">—</span>
                )}
              </div>
            );
          })}

          {/* one row per spot */}
          {rows.map((r, ri) => (
            <React.Fragment key={r.spot.id}>
              <div className="cs-rote-mb-spot" title={r.spot.name}>{shortName(r.spot)}</div>
              {cols.map((c) => {
                const s = r.byT.get(c.t);
                const top = bestByCol[c.ci] && bestByCol[c.ci].ri === ri;
                if (!s || s.ft == null) {
                  return <div key={c.t} className={`cs-rote-mb-cell cs-rote-hb-cell is-empty ${c.dayStart ? 'is-daystart' : ''}`}>·</div>;
                }
                const per = s.period != null ? Math.round(s.period) : null;
                const tide = tideShort(s.h);
                const peak = colPeak[c.ci] || { maxScore: 0, maxFt: 0 };
                const scoreRel = peak.maxScore > 0 ? s.score / peak.maxScore : 0;
                const sizeRel = peak.maxFt > 0 ? s.ft / peak.maxFt : 0;
                return (
                  <button
                    key={c.t}
                    type="button"
                    className={`cs-rote-mb-cell cs-rote-hb-cell q-${sessionQ(s.score)} ${s.fire ? 'is-fire' : ''} ${top ? 'is-top' : ''} ${s.past ? 'is-past' : ''} ${c.dayStart ? 'is-daystart' : ''} ${c.now ? 'is-now' : ''}`}
                    style={mbCellStyle({ peakFt: s.ft, score: s.score }, { scoreRel, sizeRel, top })}
                    onMouseEnter={(e) => setTip({ ri, ci: c.ci, x: e.clientX, y: e.clientY })}
                    onMouseMove={(e) => setTip({ ri, ci: c.ci, x: e.clientX, y: e.clientY })}
                  >
                    <span className="cs-rote-hb-ft" style={{ color: top ? '#ffd166' : undefined }}>{Math.round(s.ft)}′</span>
                    <span className="cs-rote-hb-meta">
                      {per != null && <em>{per}s</em>}
                      {s.dir != null && (
                        <b
                          className="cs-rote-hb-dir"
                          style={{ transform: `rotate(${arrowRotation(s.dir)}deg)` }}
                          aria-hidden
                        >
                          ↑
                        </b>
                      )}
                      {tide && <i className={`cs-rote-hb-tide t-${tide.toLowerCase()}`}>{tide}</i>}
                    </span>
                    {s.wq && <i className="cs-rote-mb-wind" style={{ background: WIND_QUALITY_COLOR[s.wq] }} title={WIND_QUALITY_LABEL[s.wq]} />}
                    {top && <em className="cs-rote-hb-crown" aria-hidden>★</em>}
                  </button>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      {activeSlot && (
        <div
          className="cs-gantt-tip"
          style={{
            left: `${Math.max(96, Math.min((typeof window !== 'undefined' ? window.innerWidth : 1200) - 96, tip.x))}px`,
            top: `${tip.y}px`,
          }}
        >
          <div className="cs-gantt-tip-time">
            {activeRow.spot.name} · {cols[tip.ci].dayLbl} {cols[tip.ci].label}
            {activeSlot.fire ? ' · 🔥 strike hour' : ''}
          </div>
          <div className="cs-meteo-tip-row">
            <span className="cs-meteo-tip-k">Face</span>
            <span className="cs-meteo-tip-v" style={{ color: swellColor(activeSlot.ft) }}>
              {fmtFt(activeSlot.ft)} · {sessionQ(activeSlot.score)}
            </span>
          </div>
          <div className="cs-meteo-tip-row">
            <span className="cs-meteo-tip-k">Swell</span>
            <span className="cs-meteo-tip-v">
              {fmtFt(activeSlot.rawFt)}{activeSlot.period != null ? ` @ ${fmtPeriod(activeSlot.period)}` : ''}
              {activeSlot.dir != null ? ` ${dirLabel(activeSlot.dir)}` : ''}
            </span>
          </div>
          <div className="cs-meteo-tip-row">
            <span className="cs-meteo-tip-k">Wind</span>
            <span className="cs-meteo-tip-v">
              <i style={{ background: (activeSlot.wq && WIND_QUALITY_COLOR[activeSlot.wq]) || '#9aa0a6' }} />
              {fmtKn(activeSlot.kn)}
              {activeSlot.h.windDir != null ? ` ${dirLabel(activeSlot.h.windDir, false)}` : ''}
              {activeSlot.wq ? ` · ${WIND_QUALITY_LABEL[activeSlot.wq]}` : ''}
            </span>
          </div>
          <div className="cs-meteo-tip-row">
            <span className="cs-meteo-tip-k">Tide</span>
            <span className="cs-meteo-tip-v">
              {activeSlot.h.tideM != null ? `${activeSlot.h.tideM}m` : '—'}
              {activeSlot.h.tideStage01 != null ? ` · ${tideStageLabel(activeSlot.h.tideStage01)}` : ''}
              {!tideOk(activeSlot.h, activeRow.spot.tide) ? ' · outside band' : ''}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------------------ mission board ------------------------------
// The cross-spot planner: one row per spot, one column per local day, each
// cell = that day's single best daylight session (the same size × wind × tide
// × swell-angle score the day cards use), so "when do I paddle out, and
// where?" is one glance. A "best call" row crowns the spot to surf each day,
// and the board headlines the standout multi-day run — the mission worth
// booking flights for.

// Peak daylight face window for the mission board. Unlike day-card "best
// windows", this ALWAYS returns a session when there's measurable surf — even
// if wind/tide/swell-angle score it as scrap — so the board stays populated
// through flat stretches. ★ Best call still requires a solid score.
function bestSessionForDay(spot, cond, day, stWindows) {
  const calib = spot.calibration?.enabled ? spot.calibration : null;
  const strike = strikeFor(spot);
  const t0 = day.hours[0].t;
  const t1 = day.hours[day.hours.length - 1].t;
  const sunrise = (cond.daily?.sunrise || []).find((s) => s >= t0 && s <= t1) ?? null;
  const sunset = (cond.daily?.sunset || []).find((s) => s >= t0 && s <= t1) ?? null;
  const isDaylight = (t) => (sunrise == null || t >= sunrise - 1800) && (sunset == null || t <= sunset + 1800);
  const slots = day.hours.map((h) => ({
    h,
    ft: surfFaceFt(h, calib),
    score: hourScore(h, spot, strike, calib, isDaylight(h.t)),
    daylight: isDaylight(h.t),
  }));

  let wins = bestWindows(slots);
  if (!wins.length) {
    // Fallback: peak daylight face (≥0.5 ft), grown into a short contiguous window.
    let bestI = -1;
    for (let i = 0; i < slots.length; i += 1) {
      const s = slots[i];
      if (!s.daylight || s.ft == null || s.ft < 0.5) continue;
      if (
        bestI < 0
        || s.score > slots[bestI].score
        || (s.score === slots[bestI].score && s.ft > slots[bestI].ft)
      ) bestI = i;
    }
    if (bestI < 0) return null;
    const peakFt0 = slots[bestI].ft;
    let lo = bestI;
    let hi = bestI;
    while (lo > 0 && slots[lo - 1].daylight && (slots[lo - 1].ft || 0) >= peakFt0 * 0.65) lo -= 1;
    while (hi < slots.length - 1 && slots[hi + 1].daylight && (slots[hi + 1].ft || 0) >= peakFt0 * 0.65) hi += 1;
    wins = [{ startIdx: lo, endIdx: hi }];
  }

  const w = wins[0];
  let peakFt = 0;
  let score = 0;
  let peakH = null;
  for (let i = w.startIdx; i <= w.endIdx; i += 1) {
    if (slots[i].score > score) score = slots[i].score;
    if ((slots[i].ft || 0) >= peakFt) { peakFt = slots[i].ft || 0; peakH = slots[i].h; }
  }
  const startT = slots[w.startIdx].h.t;
  const endT = slots[w.endIdx].h.t + 3600;
  const kn = peakH ? hourWindKn(peakH) : null;
  const wq = peakH
    ? windQuality(peakH.windDir, Number.isFinite(spot.beachFacing) ? spot.beachFacing : null, kn)
    : null;
  const fire = (stWindows || []).some((sw) => sw.endSec >= startT && sw.startSec <= endT);
  return {
    startT, endT, peakFt, score, wq, kn, fire,
    tide: peakH?.tideStage01 != null ? tideStageLabel(peakH.tideStage01) : null,
  };
}

// Session quality bucket from the hour score (drives the cell's text tone).
const sessionQ = (score) => (score >= 0.72 ? 'prime' : score >= 0.5 ? 'solid' : score >= 0.28 ? 'fair' : 'scrap');

// Compact local window label: "6–9a", "11a–2p".
function winLabel(off, a, b) {
  const f = (t) => {
    const d = new Date(t * 1000 + off);
    let h = d.getUTCHours();
    const ap = h >= 12 ? 'p' : 'a';
    h = h % 12 || 12;
    return { h, ap };
  };
  const s = f(a);
  const e = f(b);
  return s.ap === e.ap ? `${s.h}–${e.h}${e.ap}` : `${s.h}${s.ap}–${e.h}${e.ap}`;
}

/**
 * Board cell fill. Size drives hue + base presence (so low condition-scores
 * still read height). `scoreRel` / `sizeRel` (0..1 vs the column peak) lift
 * the winner and mute also-rans — steeper than absolute score alone, which
 * collapses to near-black when every spot is "scrap".
 */
function mbCellStyle(ses, { scoreRel = 1, sizeRel = 1, top = false } = {}) {
  if (!ses || ses.peakFt == null || ses.peakFt < 0.5) return {};
  const hue = swellColor(ses.peakFt);
  const sizeA = Math.min(1, 0.38 + ses.peakFt / 11); // ~4′→0.74, ~8′→1.0
  const sr = Math.max(0.1, Math.min(1, Number.isFinite(scoreRel) ? scoreRel : 1));
  const fr = Math.max(0.15, Math.min(1, Number.isFinite(sizeRel) ? sizeRel : 1));
  // Prefer condition rank; when scores cluster, face height still separates.
  const rank = Math.max(sr, fr * 0.9);
  const lift = 0.18 + 0.82 * (rank ** 1.25);
  const aTop = Math.min(0.97, sizeA * lift * (top ? 1.12 : 0.78));
  const aBot = Math.min(0.62, aTop * 0.32);
  const pTop = Math.round(aTop * 100);
  const pBot = Math.round(Math.max(12, aBot * 100));
  if (top) {
    return {
      background: [
        'linear-gradient(165deg,',
        `color-mix(in srgb, #ffd166 42%, ${hue}) 0%,`,
        `color-mix(in srgb, ${hue} ${pTop}%, transparent) 45%,`,
        `color-mix(in srgb, ${hue} ${pBot}%, transparent) 100%)`,
      ].join(' '),
    };
  }
  return {
    background: [
      'linear-gradient(180deg,',
      `color-mix(in srgb, ${hue} ${pTop}%, transparent) 0%,`,
      `color-mix(in srgb, ${hue} ${pBot}%, transparent) 100%)`,
    ].join(' '),
  };
}

function MissionBoard({ spots, fmap }) {
  const [tip, setTip] = useState(null); // { ri, key, x, y }

  const { rows, cols } = useMemo(() => {
    const nowSec = Date.now() / 1000;
    const built = spots
      .map((spot) => {
        const raw = fmap[spot.id]?.cond;
        if (!raw?.hours?.length) return null;
        const hours = densifyForecastHours(raw.hours);
        const cond = { ...raw, hours };
        const calib = spot.calibration?.enabled ? spot.calibration : null;
        const beachFacing = Number.isFinite(spot.beachFacing) ? spot.beachFacing : null;
        const strike = strikeFor(spot);
        const stEval = strike.enabled !== false
          ? evaluateStrike(cond, strike, beachFacing, calib, spot.tide)
          : { windows: [] };
        const days = groupByDay(cond)
          .filter((d) => d.hours[d.hours.length - 1].t >= nowSec - 3600)
          .slice(0, FORECAST_DAYS);
        const dayByKey = new Map(days.map((d) => [d.key, d]));
        return { spot, cond, off: (cond.utcOffsetSec || 0) * 1000, days, dayByKey, stEval };
      })
      .filter(Boolean);

    if (!built.length) return { rows: [], cols: [] };

    // Shared day columns = union across spots (so a thin series still lines up).
    const dayMeta = new Map(); // key -> { t0, t1, off }
    built.forEach((r) => {
      r.days.forEach((d) => {
        if (!dayMeta.has(d.key)) {
          dayMeta.set(d.key, {
            key: d.key,
            t0: d.hours[0].t,
            t1: d.hours[d.hours.length - 1].t,
            off: r.off,
          });
        }
      });
    });
    const dayKeys = [...dayMeta.keys()].sort((a, b) => dayMeta.get(a).t0 - dayMeta.get(b).t0);

    const rowsOut = built.map((r) => {
      const cells = new Map();
      dayKeys.forEach((key) => {
        let day = r.dayByKey.get(key);
        if (!day) {
          // Build a synthetic local day from this spot's densified hours so every
          // column gets a chance to show a session.
          const meta = dayMeta.get(key);
          const hours = r.cond.hours.filter((h) => {
            const k = Math.floor((h.t * 1000 + r.off) / 86_400_000);
            return k === key;
          });
          if (!hours.length) {
            cells.set(key, { day: null, ses: null });
            return;
          }
          day = { key, hours, startMs: hours[0].t * 1000, endMs: hours[hours.length - 1].t * 1000 };
        }
        cells.set(key, { day, ses: bestSessionForDay(r.spot, r.cond, day, r.stEval.windows) });
      });
      return {
        spot: r.spot,
        cond: r.cond,
        off: r.off,
        days: dayKeys.map((k) => r.dayByKey.get(k)).filter(Boolean),
        cells,
      };
    });

    const colsOut = dayKeys.map((key, i) => {
      const meta = dayMeta.get(key);
      const ld = new Date(meta.t0 * 1000 + meta.off);
      return {
        key,
        idx: i,
        dow: ld.toLocaleDateString(undefined, { weekday: 'short', timeZone: 'UTC' }),
        dom: ld.getUTCDate(),
        today: nowSec >= meta.t0 && nowSec <= meta.t1 + 3600,
      };
    });
    return { rows: rowsOut, cols: colsOut };
  }, [spots, fmap]);

  // Best call per day (across spots). Always crown a relative winner when there's
  // measurable surf; "rest" only when every spot is empty that day.
  const bestByCol = useMemo(() => cols.map((c) => {
    let best = null;
    rows.forEach((r, ri) => {
      const ses = r.cells.get(c.key)?.ses;
      if (!ses || ses.peakFt < 0.5) return;
      if (
        !best
        || ses.score > best.ses.score
        || (ses.score === best.ses.score && ses.peakFt > best.ses.peakFt)
      ) best = { ri, ses, spot: r.spot, off: r.off };
    });
    return best;
  }), [cols, rows]);

  const colPeak = useMemo(() => cols.map((c) => {
    let maxScore = 0;
    let maxFt = 0;
    rows.forEach((r) => {
      const ses = r.cells.get(c.key)?.ses;
      if (!ses) return;
      if (ses.score > maxScore) maxScore = ses.score;
      if (ses.peakFt > maxFt) maxFt = ses.peakFt;
    });
    return { maxScore, maxFt };
  }), [cols, rows]);

  const mission = useMemo(() => {
    // Contiguous days whose best call scores solid-or-better, ranked by total.
    // (Crown always picks a relative winner; the mission still wants real quality.)
    const runs = [];
    let cur = null;
    bestByCol.forEach((b, i) => {
      if (b && b.ses.score >= 0.5) {
        if (cur && i === cur.end + 1) cur.end = i;
        else { cur = { start: i, end: i }; runs.push(cur); }
      } else cur = null;
    });
    if (!runs.length) return null;
    runs.forEach((r) => {
      r.sum = 0;
      for (let i = r.start; i <= r.end; i += 1) r.sum += bestByCol[i].ses.score;
    });
    runs.sort((a, b) => b.sum - a.sum);
    const r = runs[0];
    const names = new Map();
    let maxFt = 0;
    for (let i = r.start; i <= r.end; i += 1) {
      const b = bestByCol[i];
      names.set(b.spot.name, (names.get(b.spot.name) || 0) + 1);
      maxFt = Math.max(maxFt, b.ses.peakFt);
    }
    const spotLbl = [...names.entries()].sort((a, b) => b[1] - a[1]).map(([n]) => n).slice(0, 2).join(' + ');
    const days = r.end - r.start + 1;
    return { ...r, days, spotLbl, maxFt };
  }, [bestByCol]);

  if (!rows.length || !cols.length) return null;

  const gridCols = { gridTemplateColumns: `88px repeat(${cols.length}, minmax(0, 1fr))` };
  const shortName = (s) => s.name.replace(/\s*\(.*\)$/, '');
  const activeRow = tip ? rows[tip.ri] : null;
  const activeCell = tip && activeRow ? activeRow.cells.get(tip.key) : null;

  return (
    <div className="cs-rote-mission" onMouseLeave={() => setTip(null)}>
      <div className="cs-rote-mission-head">
        <h2>Mission board</h2>
        <span className="cs-help">
          Best daylight session per spot per day. Gradient lifts the day’s best call (gold edge);
          dim scrap = wrong angle / blown / flat. ★ = best call · 🔥 = full strike.
        </span>
        {mission && (
          <span className="cs-rote-mission-callout" title="The standout contiguous run of best-call days">
            🎯 {mission.days >= 2
              ? `Load the boat: ${cols[mission.start].dow} ${cols[mission.start].dom} – ${cols[mission.end].dow} ${cols[mission.end].dom}`
              : `Best day: ${cols[mission.start].dow} ${cols[mission.start].dom}`}
            {' · '}{mission.spotLbl} · up to {Math.round(mission.maxFt)} ft
          </span>
        )}
      </div>

      <div className="cs-rote-mb-scroll">
        <div className="cs-rote-mb" style={gridCols}>
          {/* day header */}
          <div className="cs-rote-mb-corner" />
          {cols.map((c) => (
            <div key={c.key} className={`cs-rote-mb-dayhead ${c.today ? 'is-today' : ''} ${c.idx >= CONFIDENCE_DAYS ? 'is-ext' : ''}`}>
              <span>{c.dow}</span>
              <b>{c.dom}</b>
            </div>
          ))}

          {/* best-call row */}
          <div className="cs-rote-mb-spot cs-rote-mb-bestlbl">★ Best call</div>
          {cols.map((c, i) => {
            const b = bestByCol[i];
            return (
              <div key={c.key} className={`cs-rote-mb-best ${c.idx >= CONFIDENCE_DAYS ? 'is-ext' : ''}`}>
                {b ? (
                  <>
                    <b className={`q-${sessionQ(b.ses.score)}`}>{shortName(b.spot)}</b>
                    <span>{winLabel(b.off, b.ses.startT, b.ses.endT)}</span>
                  </>
                ) : (
                  <span className="cs-rote-mb-skip">—</span>
                )}
              </div>
            );
          })}

          {/* one row per spot */}
          {rows.map((r, ri) => (
            <React.Fragment key={r.spot.id}>
              <div className="cs-rote-mb-spot" title={r.spot.name}>{shortName(r.spot)}</div>
              {cols.map((c, i) => {
                const cell = r.cells.get(c.key);
                const ses = cell?.ses || null;
                const top = bestByCol[i] && bestByCol[i].ri === ri;
                if (!ses) {
                  return <div key={c.key} className={`cs-rote-mb-cell is-empty ${c.idx >= CONFIDENCE_DAYS ? 'is-ext' : ''}`}>—</div>;
                }
                const peak = colPeak[i] || { maxScore: 0, maxFt: 0 };
                const scoreRel = peak.maxScore > 0 ? ses.score / peak.maxScore : 0;
                const sizeRel = peak.maxFt > 0 ? ses.peakFt / peak.maxFt : 0;
                return (
                  <button
                    key={c.key}
                    type="button"
                    className={`cs-rote-mb-cell q-${sessionQ(ses.score)} ${top ? 'is-top' : ''} ${c.idx >= CONFIDENCE_DAYS ? 'is-ext' : ''}`}
                    style={mbCellStyle(ses, { scoreRel, sizeRel, top })}
                    onMouseEnter={(e) => setTip({ ri, key: c.key, x: e.clientX, y: e.clientY })}
                    onMouseMove={(e) => setTip({ ri, key: c.key, x: e.clientX, y: e.clientY })}
                  >
                    <span className="cs-rote-mb-win">{winLabel(r.off, ses.startT, ses.endT)}</span>
                    <span className="cs-rote-mb-ft">
                      {Math.round(ses.peakFt)}
                      {ses.fire && <em className="cs-rote-mb-fire">🔥</em>}
                    </span>
                    {ses.wq && <i className="cs-rote-mb-wind" style={{ background: WIND_QUALITY_COLOR[ses.wq] }} />}
                    {top && <em className="cs-rote-mb-star">★</em>}
                  </button>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      {activeCell?.ses && (
        <div
          className="cs-gantt-tip"
          style={{
            left: `${Math.max(96, Math.min((typeof window !== 'undefined' ? window.innerWidth : 1200) - 96, tip.x))}px`,
            top: `${tip.y}px`,
          }}
        >
          <div className="cs-gantt-tip-time">
            {activeRow.spot.name} · {cols.find((c) => c.key === tip.key)?.dow} {cols.find((c) => c.key === tip.key)?.dom}
            {activeCell.ses.fire ? ' · 🔥 strike window' : ''}
          </div>
          <div className="cs-meteo-tip-row">
            <span className="cs-meteo-tip-k">Session</span>
            <span className="cs-meteo-tip-v">
              {winLabel(activeRow.off, activeCell.ses.startT, activeCell.ses.endT)} · {sessionQ(activeCell.ses.score)}
            </span>
          </div>
          <div className="cs-meteo-tip-row">
            <span className="cs-meteo-tip-k">Face</span>
            <span className="cs-meteo-tip-v" style={{ color: swellColor(activeCell.ses.peakFt) }}>
              up to {fmtFt(activeCell.ses.peakFt)}
            </span>
          </div>
          <div className="cs-meteo-tip-row">
            <span className="cs-meteo-tip-k">Wind</span>
            <span className="cs-meteo-tip-v">
              <i style={{ background: (activeCell.ses.wq && WIND_QUALITY_COLOR[activeCell.ses.wq]) || '#9aa0a6' }} />
              {fmtKn(activeCell.ses.kn)}{activeCell.ses.wq ? ` · ${WIND_QUALITY_LABEL[activeCell.ses.wq]}` : ''}
            </span>
          </div>
          {activeCell.ses.tide && (
            <div className="cs-meteo-tip-row">
              <span className="cs-meteo-tip-k">Tide</span>
              <span className="cs-meteo-tip-v">{activeCell.ses.tide} at the peak</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SpotRow({ spot, entry, canEdit }) {
  const [showSettings, setShowSettings] = useState(false);
  const cond = entry?.cond || null;
  const nowKn = useMemo(() => {
    if (!cond?.hours?.length) return null;
    const nowSec = Date.now() / 1000;
    const h = cond.hours.find((x) => x.t >= nowSec) || cond.hours[cond.hours.length - 1];
    return kmhToKn(h.windSpd);
  }, [cond]);
  return (
    <div className="cs-rote-spot">
      <div className="cs-rote-spot-head">
        <div className="cs-rote-spot-name">
          <h3>{spot.name}</h3>
          <PrefChips spot={spot} />
        </div>
        <div className="cs-fc-head-actions">
          {nowKn != null && <span className="cs-rote-spot-now">wind now {fmtKn(nowKn)}</span>}
          {canEdit && (
            <>
              <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setShowSettings((s) => !s)} title="Preferences: strike bar, wind & swell arcs, tide, calibration">⚙</button>
              <button
                className="cs-btn cs-btn-ghost cs-btn-sm"
                onClick={() => { if (window.confirm(`Stop tracking ${spot.name}?`)) removeSpot(spot.id); }}
                title="Remove spot"
              >
                ✕
              </button>
            </>
          )}
        </div>
      </div>
      {canEdit && showSettings && <SpotSettings spot={spot} />}
      {!entry || entry.loading ? (
        <div className="cs-fc-loading">Loading forecast…</div>
      ) : entry.err ? (
        <div className="cs-fc-err">Couldn’t load forecast: {entry.err}</div>
      ) : cond ? (
        <>
          <TwoDayPanel spot={spot} cond={cond} />
          <StrikeStrip spot={spot} cond={cond} />
        </>
      ) : null}
    </div>
  );
}

function AddRoteSpotForm({ onClose, count }) {
  const [form, setForm] = useState({ name: '', region: 'Rote, Indonesia', lat: '', lng: '', beachFacing: '' });
  const [busy, setBusy] = useState(false);
  const valid = form.name.trim() && Number.isFinite(Number(form.lat)) && Number.isFinite(Number(form.lng));
  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      await addSpot(form, count);
      onClose();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="cs-fc-addform">
      <div className="cs-fc-addrow">
        <input className="cs-input cs-input-sm" placeholder="Spot name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input className="cs-input cs-input-sm" placeholder="Region" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} />
        <input className="cs-input cs-input-sm" placeholder="Latitude" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} />
        <input className="cs-input cs-input-sm" placeholder="Longitude" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} />
        <input className="cs-input cs-input-sm" placeholder="Beach faces° (optional)" value={form.beachFacing} onChange={(e) => setForm({ ...form, beachFacing: e.target.value })} />
        <button className="cs-btn cs-btn-primary cs-btn-sm" disabled={!valid || busy} onClick={submit}>Add</button>
        <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={onClose}>Cancel</button>
      </div>
      <div className="cs-help" style={{ margin: '6px 0 0' }}>
        Keep “Rote” in the region so it shows on this page. After adding, open ⚙ to set the swell
        window, tolerable wind arc, tide band and a calibration anchor.
      </div>
    </div>
  );
}

export default function Rote() {
  const { user } = useAuth();
  const allSpots = useForecastSpots();
  const spots = useMemo(() => (allSpots || []).filter(isRoteSpot), [allSpots]);
  const [adding, setAdding] = useState(false);

  const { basins } = useSwellMaps();
  const roteDoc = basins.rote || null;
  const roteSwDoc = basins['rote-sw'] || null;

  const fmap = useRoteForecasts(spots);

  // Top up newly shipped default spots (T-Land / Bo'a) + calibrations for
  // signed-in users, same idempotent path the Forecast page uses.
  useEffect(() => {
    if (!user || !allSpots || allSpots.length === 0) return;
    ensureDefaultSpots(allSpots).catch(() => {});
    ensureSpotCalibrations(allSpots).catch(() => {});
  }, [user, allSpots]);

  const isHome = SITE.home.page === 'rote';
  // Column widths proportional to each video's aspect ratio ⇒ both maps render
  // at exactly the same height (the local SW-tip map sets the scale).
  const ratio = (d, fb) => (d?.width && d?.height ? d.width / d.height : fb);
  const mapsCols = { '--rote-maps-cols': `${ratio(roteSwDoc, 1.43)}fr ${ratio(roteDoc, 1.13)}fr` };
  return (
    <div className="cs-page cs-rote-page">
      {(!isHome || user) && (
        <div className="cs-fc-header cs-rote-tophead">
          <div>{!isHome && <h1>Rote — Strike Missions</h1>}</div>
          {user && (
            <button className="cs-btn cs-btn-primary cs-btn-sm" onClick={() => setAdding((a) => !a)}>
              {adding ? 'Close' : '+ Add spot'}
            </button>
          )}
        </div>
      )}

      {user && adding && <AddRoteSpotForm onClose={() => setAdding(false)} count={allSpots?.length || 0} />}

      <div className="cs-rote-maps" style={mapsCols}>
        <div className="cs-rote-maps-col">
          <div className="cs-rote-map-caption">
            <b>Rote — SW tip</b> — Nemberala · Bo’a · Ndao · Ndana. Copernicus 1/12° swell over
            GEBCO bathymetry, reef-level coastline.
          </div>
          <RegionalMap doc={roteSwDoc} spots={spots} bathyUrl={BATHY_URL_SW} />
        </div>
        <div className="cs-rote-maps-col">
          <div className="cs-rote-map-caption">
            <b>Regional</b> — Savu Sea / Timor gap. How each swell train enters the basin.
          </div>
          <RegionalMap doc={roteDoc} spots={spots} bathyUrl={BATHY_URL_REGIONAL} />
        </div>
      </div>

      {spots.length > 0 && <HourlyBoard spots={spots} fmap={fmap} />}

      {spots.length > 0 && <MissionBoard spots={spots} fmap={fmap} />}

      <div className="cs-rote-legend cs-help">
        Each cell is a local day — number = peak face (ft), arrow = swell direction, dot = wind
        quality at the peak, 🔥 = a strike window (size + period + swell angle + wind + tide all
        inside this spot’s preferences). Hatched days are the lower-confidence extended outlook.
      </div>

      {allSpots === null ? (
        <div className="cs-fc-loading">Loading spots…</div>
      ) : spots.length === 0 ? (
        <div className="cs-empty">No Rote spots tracked yet{user ? ' — sign-in seeding will add T-Land and Bo’a shortly.' : '.'}</div>
      ) : (
        spots.map((s) => <SpotRow key={s.id} spot={s} entry={fmap[s.id]} canEdit={!!user} />)
      )}
    </div>
  );
}
